import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Command } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/");
    } catch (err) {
      setError(apiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      <div className="flex-1 flex items-center justify-center p-6 bg-white">
        <form onSubmit={submit} className="w-full max-w-sm fade-up" data-testid="login-form">
          <div className="flex items-center gap-2 mb-10">
            <div className="w-9 h-9 bg-slate-950 text-white flex items-center justify-center rounded-sm">
              <Command size={20} />
            </div>
            <span className="font-black tracking-tighter text-xl">EXECUTIVE OS</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-black tracking-tighter text-slate-950">Sign in.</h1>
          <p className="text-slate-600 mt-2 mb-8">Run your work from one operational command center.</p>

          {error && (
            <div className="mb-4 text-sm text-destructive bg-destructive/10 px-3 py-2 rounded-sm" data-testid="login-error">
              {error}
            </div>
          )}

          <label className="label-mono text-slate-500 block mb-1.5">Email</label>
          <Input value={email} onChange={(e) => setEmail(e.target.value)} type="email" required
            className="rounded-sm mb-4" data-testid="login-email-input" placeholder="ceo@executiveos.com" />

          <label className="label-mono text-slate-500 block mb-1.5">Password</label>
          <Input value={password} onChange={(e) => setPassword(e.target.value)} type="password" required
            className="rounded-sm mb-6" data-testid="login-password-input" placeholder="••••••••" />

          <Button type="submit" disabled={loading}
            className="w-full rounded-sm bg-slate-950 hover:bg-slate-800 h-11" data-testid="login-submit-button">
            {loading ? "Signing in…" : "Sign in"}
          </Button>

          <p className="text-sm text-slate-600 mt-6">
            New here?{" "}
            <Link to="/register" className="text-ai font-medium" data-testid="goto-register-link">
              Create a workspace
            </Link>
          </p>
        </form>
      </div>
      <div
        className="hidden md:block flex-1 bg-cover bg-center relative"
        style={{ backgroundImage: "url('https://images.pexels.com/photos/21327037/pexels-photo-21327037.jpeg')" }}
      >
        <div className="absolute inset-0 bg-slate-950/30" />
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <div className="label-mono text-white/70 mb-3">Executive Productivity Suite</div>
          <p className="text-3xl font-bold tracking-tight max-w-md">
            Tasks, notes, voice, calendar & an AI copilot — one place to operate.
          </p>
        </div>
      </div>
    </div>
  );
}
